import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import DropzoneComponent from 'components/Dropzone';
import FieldRow from 'components/FormElements/FieldRow';
import RecordView from 'components/RecordView';
import ButtonGroup from 'components/ButtonGroup';
import { FileType } from 'further-ui/utils';
import { TranchePromotion } from '.';

const acceptedFundDocumentFileTypes: Record<string, Array<string>> = {
  [FileType.DOCX]: ['.docx'],
  [FileType.PDF]: ['.pdf'],
  [FileType.JPGImage]: ['.jpg', '.jpeg'],
  [FileType.PNGImage]: ['.png'],
};

const useStyles = makeStyles()(() => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

interface Props {
  handleCancel: () => void;
  handleClickTranchePromotion: () => void;
  id: string;
  onChangeTranchePromotion: (
    files: Array<File>,
    field: string,
  ) => Promise<void>;
  setTranchePromotion: (tranchePromotion: TranchePromotion) => void;
  tranchePromotion: TranchePromotion;
  disabled: boolean;
  isSyndicate: boolean;
  isLP: boolean;
  additionalDocuments: Array<{
    name: string;
    preview: string;
  }>;
  handleChangeAdditionalDocuments: (files: Array<File>) => Promise<void>;
  handleRemoveAdditionalDocuments: (index: number) => void;
  viewOnly: boolean;
}

export default function TranchePromotionInformation({
  handleCancel,
  handleClickTranchePromotion,
  id,
  onChangeTranchePromotion,
  setTranchePromotion,
  tranchePromotion,
  disabled = false,
  isSyndicate = false,
  isLP = false,
  additionalDocuments,
  handleChangeAdditionalDocuments,
  handleRemoveAdditionalDocuments,
  viewOnly = false,
}: Props) {
  const { classes } = useStyles();

  return (
    <RecordView>
      {isSyndicate ? (
        <FieldRow
          title="Deal documentation"
          tooltipText="Please upload all documentation relating to this opportunity that you want investors to acknowledge or agree to."
        >
          <DropzoneComponent
            name="dealDocumentation"
            accept={acceptedFundDocumentFileTypes}
            onDropFile={handleChangeAdditionalDocuments}
            onRemove={handleRemoveAdditionalDocuments}
            maxFiles={10}
            disabled={viewOnly}
            files={additionalDocuments.map((file) => ({
              fileName: file.name,
              preview: file.preview,
            }))}
          />
        </FieldRow>
      ) : (
        <FieldRow
          title={`Legal agreement*`}
          tooltipText="Please upload your subscription management agreement, limited partnership agreement or, if you have a prospectus, your application form."
        >
          <DropzoneComponent
            name="applicationFormFiles"
            accept={acceptedFundDocumentFileTypes}
            onDropFile={(files) =>
              onChangeTranchePromotion(files, 'applicationFormFile')
            }
            disabled={viewOnly}
            files={
              tranchePromotion?.applicationFormFile
                ? [
                    {
                      preview: tranchePromotion?.applicationFormFile.preview,
                      fileName: tranchePromotion?.applicationFormFile.name,
                    },
                  ]
                : []
            }
            onRemove={() => {
              setTranchePromotion({
                ...tranchePromotion,
                applicationFormFile: null,
              });
            }}
          />
        </FieldRow>
      )}

      {isLP ? (
        <>
          <FieldRow
            title="Additional documents"
            tooltipText="Upload any other fund documentation here, for example your PPM, fundraising presentation, FAQ document, KID or other documents you wish to share."
          >
            <DropzoneComponent
              name="additionalDocuments"
              accept={acceptedFundDocumentFileTypes}
              onDropFile={handleChangeAdditionalDocuments}
              onRemove={handleRemoveAdditionalDocuments}
              maxFiles={10}
              disabled={viewOnly}
              files={additionalDocuments.map((file) => ({
                fileName: file.name,
                preview: file.preview,
              }))}
            />
          </FieldRow>
        </>
      ) : (
        !isSyndicate && (
          <>
            <FieldRow
              title="Offering document"
              tooltipText="Please upload your core fund marketing document. This may be a prospectus, information memorandum or key fundraising presentation."
            >
              <DropzoneComponent
                name="prospectusFiles"
                accept={acceptedFundDocumentFileTypes}
                onDropFile={(files) =>
                  onChangeTranchePromotion(files, 'prospectusFile')
                }
                disabled={viewOnly}
                files={
                  tranchePromotion?.prospectusFile
                    ? [
                        {
                          preview: tranchePromotion?.prospectusFile.preview,
                          fileName: tranchePromotion?.prospectusFile.name,
                        },
                      ]
                    : []
                }
                onRemove={() => {
                  setTranchePromotion({
                    ...tranchePromotion,
                    prospectusFile: null,
                  });
                }}
              />
            </FieldRow>
            <FieldRow
              title="Key information document"
              tooltipText="Please upload your Key Information Document (KID) if you have been required to produce one."
            >
              <DropzoneComponent
                name="keyInfoFiles"
                accept={acceptedFundDocumentFileTypes}
                onDropFile={(files) =>
                  onChangeTranchePromotion(files, 'keyInfoFile')
                }
                disabled={viewOnly}
                files={
                  tranchePromotion?.keyInfoFile
                    ? [
                        {
                          preview: tranchePromotion?.keyInfoFile.preview,
                          fileName: tranchePromotion?.keyInfoFile.name,
                        },
                      ]
                    : []
                }
                onRemove={() => {
                  setTranchePromotion({
                    ...tranchePromotion,
                    keyInfoFile: null,
                  });
                }}
              />
            </FieldRow>
          </>
        )
      )}

      {!viewOnly && (
        <ButtonGroup>
          <Button
            className={classes.marginLeftBtn}
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            className={classes.marginLeftBtn}
            variant="contained"
            color="primary"
            disabled={
              (!isSyndicate && !tranchePromotion?.applicationFormFile) ||
              disabled
            }
            onClick={handleClickTranchePromotion}
          >
            {id ? 'Update' : 'Save'}
          </Button>
        </ButtonGroup>
      )}
    </RecordView>
  );
}
